import { ReactNode, useContext, useEffect, useMemo } from 'react';
import {
  AllieBellOutlineIcon,
  AllieCoinsStackedIcon,
  AllieFactoryGptIcon,
} from '@allie/app-ui-lib';
import { useQuery } from '@apollo/client';
import { cogOutline, speedometerOutline, statsChartOutline } from 'ionicons/icons';
import { findPermissionWithAction } from '../helpers/permissionsChecking';
import { PermissionAction, PermissionSubject } from '../helpers/enums';
import { useFeatureFlag, useReportRead } from '.';
import type { menuGroups } from '../components/Menu/Menu.mobile';
import { useAppTranslations } from './useAppTranslations';
import { isMobile } from '../helpers/utils';
import { config } from '../helpers/configs';
import { GET_MENU_STATIONS } from '../data';
import { AuthContext } from '../context';
import { useNotification } from 'src/context/NotificationProvider';

export interface AppPage {
  url: string;
  iosIcon?: string;
  mdIcon?: string;
  customIcon?: ReactNode;
  title: string;
  permissionRequired?: [PermissionSubject, PermissionAction];
  group?: menuGroups;
  children?: AppPage[];
  offline?: boolean;
  badgeNumber?: number;
}

const EmptyIcon = () => <div id="empty-icon-menu" style={{ width: '16px' }}></div>;

export const useRoutes = (): AppPage[] => {
  const { isCompany, isTest } = useFeatureFlag();
  const { totalUnseenNotifications, refetch, unseenReportsTotal, unseenAlertsTotal } =
    useNotification();
  useReportRead(refetch);
  const { permissions } = useContext(AuthContext);
  const {
    MENU_CONFIGURATIONS,
    MENU_FACTORY_GPT,
    MENU_FACTORY_SUMMARY,
    MENU_LINE_MONITORING,
    MENU_SCRAP,
    MENU_ALERTS,
    SEE_ALL,
  } = useAppTranslations();
  const { data, refetch: refetchStations } = useQuery(GET_MENU_STATIONS, {
    variables: {
      params: {
        page: 0,
        limit: config.OPTIONS_FETCH_LIMIT,
      },
    },
  });
  const { loggedUser } = useContext(AuthContext);

  useEffect(() => {
    refetchStations();
  }, [loggedUser]);

  const accountPermissionsSubPage = [
    PermissionSubject.CONFIG_ACCOUNT_STATIONS,
    PermissionSubject.CONFIG_ACCOUNT_USERS,
    PermissionSubject.CONFIG_ACCOUNT_PRODUCTS,
    PermissionSubject.CONFIG_ACCOUNT_MACHINES,
    PermissionSubject.CONFIG_ACCOUNT_DOWNTIME_REASONS,
    PermissionSubject.CONFIG_ACCOUNT_QUALITY_REASONS,
    PermissionSubject.CONFIG_ACCOUNT_BULK_DATA,
  ];

  const gatewayAndMetricsPermissionsSubPage = [
    PermissionSubject.CONFIG_GATEWAYS,
    PermissionSubject.CONFIG_METRICS,
  ];

  const accountPermissionsPage = [
    ...accountPermissionsSubPage,
    ...gatewayAndMetricsPermissionsSubPage,
  ];

  const scrapAndCostPage = [PermissionSubject.SCRAP, PermissionSubject.COST];

  const stationsPages = useMemo(() => {
    if (!data?.stations.stations) {
      return [];
    }

    return data.stations.stations.map((station: { id: number; name: string }) => ({
      title: station.name,
      url: `shift/dashboard?stationId=${station.id}`,
      permissionRequired: [PermissionSubject.STATION, PermissionAction.VIEW],
      children: [
        {
          title: MENU_LINE_MONITORING.SUB_MENU_PRODUCTION,
          url: `/efficiency/shift/dashboard?stationId=${station.id}`,
          permissionRequired: [PermissionSubject.STATION, PermissionAction.VIEW],
        },
        {
          title: MENU_LINE_MONITORING.SUB_MENU_MACHINES,
          url: `/efficiency/shift/subprocesses?stationId=${station.id}`,
          permissionRequired: [PermissionSubject.PROCESS_VARIABLES, PermissionAction.VIEW],
        },
        {
          title: MENU_LINE_MONITORING.SUB_MENU_PROCESS_VARIABLES,
          url: `/efficiency/shift/process-variables?stationId=${station.id}`,
          permissionRequired: [PermissionSubject.PROCESS_VARIABLES, PermissionAction.VIEW],
        },
        ...(isTest
          ? [
              {
                title: MENU_LINE_MONITORING.SUB_MENU_PREDICTIONS,
                url: `/efficiency/shift/prediction?stationId=${station.id}`,
                permissionRequired: [PermissionSubject.PREDICTIONS, PermissionAction.VIEW],
              },
            ]
          : []),
        ...(isMobile
          ? []
          : [
              {
                title: MENU_LINE_MONITORING.SUB_MENU_REPORTS,
                url: `/efficiency/shift/reports?stationId=${station.id}`,
                permissionRequired: [PermissionSubject.STATION, PermissionAction.VIEW],
              },
            ]),
      ],
    }));
  }, [data, isMobile]);

  const factoryGptPages: AppPage[] = [
    {
      title: MENU_FACTORY_GPT.SUB_MENU_QUERIES,
      url: '/factory-gpt/queries',
      permissionRequired: [PermissionSubject.FACTORY_GPT, PermissionAction.VIEW],
    },
    {
      title: MENU_FACTORY_GPT.SUB_MENU_REPORTS,
      url: '/factory-gpt/reports/weekly',
      permissionRequired: [PermissionSubject.FACTORY_GPT, PermissionAction.VIEW],
      badgeNumber: unseenReportsTotal || undefined,
    },
  ];

  const factoryPagesDesktop: AppPage[] = [
    {
      title: MENU_FACTORY_SUMMARY.SUB_MENU_EFFICIENCY,
      url: '/efficiency/factory/efficiency',
      permissionRequired: [PermissionSubject.COMPANY_DASHBOARD, PermissionAction.VIEW],
    },
    {
      title: MENU_FACTORY_SUMMARY.SUB_MENU_AVAILABILITY,
      url: '/efficiency/factory/availability',
      permissionRequired: [PermissionSubject.COMPANY_DASHBOARD, PermissionAction.VIEW],
    },
    {
      title: isCompany('bafar-app')
        ? MENU_FACTORY_SUMMARY.SUB_MENU_COMPLIANT_PRODUCT
        : MENU_FACTORY_SUMMARY.SUB_MENU_QUALITY,
      url: '/efficiency/factory/quality',
      permissionRequired: [PermissionSubject.COMPANY_DASHBOARD, PermissionAction.VIEW],
    },
    {
      title: MENU_FACTORY_SUMMARY.SUB_MENU_ECONOMIC_IMPACT,
      url: '/efficiency/factory/economic-impact',
      permissionRequired: [PermissionSubject.COMPANY_DASHBOARD, PermissionAction.VIEW],
    },
    {
      title: MENU_FACTORY_SUMMARY.SUB_MENU_REPORTS,
      url: '/efficiency/factory/report',
      permissionRequired: [PermissionSubject.COMPANY_DASHBOARD, PermissionAction.VIEW],
    },
  ];

  const alertsPages: AppPage[] = [
    {
      title: MENU_ALERTS.SUB_MENU_ALERTS,
      url: '/factory-gpt/alerts',
      permissionRequired: [PermissionSubject.FACTORY_GPT, PermissionAction.VIEW],
    },
    {
      title: MENU_ALERTS.SUB_MENU_NOTIFICATIONS,
      url: '/notifications',
      permissionRequired: [PermissionSubject.FACTORY_GPT, PermissionAction.VIEW],
    },
  ];

  const getCommonPages = (): AppPage[] => [
    {
      title: MENU_FACTORY_GPT.MENU,
      url: '/factory-gpt',
      customIcon: <AllieFactoryGptIcon width={'16'} />,
      permissionRequired: [PermissionSubject.FACTORY_GPT, PermissionAction.VIEW],
      badgeNumber: totalUnseenNotifications,
      children: factoryGptPages,
    },
    {
      title: MENU_ALERTS.MENU,
      url: '/factory-gpt',
      customIcon: <AllieBellOutlineIcon allieColor="neutral-30" />,
      permissionRequired: [PermissionSubject.FACTORY_GPT, PermissionAction.VIEW] as [
        PermissionSubject,
        PermissionAction,
      ],
      badgeNumber: unseenAlertsTotal || undefined,
      children: alertsPages,
    },
    {
      title: MENU_LINE_MONITORING.MENU,
      url: '/efficiency',
      iosIcon: speedometerOutline,
      mdIcon: speedometerOutline,
      ...(loggedUser?.role !== 'guest' && {
        children: [
          {
            title: SEE_ALL,
            url: `/efficiency/stations/dashboard`,
            customIcon: <EmptyIcon />,
            permissionRequired: [PermissionSubject.STATIONS_DASHBOARD, PermissionAction.VIEW],
          },
          ...stationsPages,
        ],
      }),
    },
    {
      title: MENU_FACTORY_SUMMARY.MENU,
      url: '/efficiency/factory',
      iosIcon: statsChartOutline,
      mdIcon: statsChartOutline,
      children: [
        ...(!isMobile ? factoryPagesDesktop : []),
        {
          title: MENU_FACTORY_SUMMARY.SUB_MENU_OVERVIEW,
          url: '/efficiency/factory-v2/overview',
          permissionRequired: [PermissionSubject.COMPANY_DASHBOARD, PermissionAction.VIEW],
        },
      ],
    },
  ];

  const getDesktopPages = (): AppPage[] => {
    const basePages = [
      ...getCommonPages(),
      {
        title: MENU_CONFIGURATIONS.MENU,
        url: '/configurations',
        iosIcon: cogOutline,
        mdIcon: cogOutline,
        permissionRequired: [
          findPermissionWithAction(accountPermissionsPage, PermissionAction.VIEW, permissions),
          PermissionAction.VIEW,
        ],
        children: [
          {
            title: MENU_CONFIGURATIONS.SUB_MENU_ACCOUNT,
            url: '/configurations/account',
            permissionRequired: [
              findPermissionWithAction(
                accountPermissionsSubPage,
                PermissionAction.VIEW,
                permissions
              ),
              PermissionAction.VIEW,
            ],
          },
          {
            title: MENU_CONFIGURATIONS.SUB_MENU_METRICS,
            url: '/configurations/metrics',
            permissionRequired: [
              findPermissionWithAction(
                gatewayAndMetricsPermissionsSubPage,
                PermissionAction.VIEW,
                permissions
              ),
              PermissionAction.VIEW,
            ],
          },
        ],
      },
      {
        title: MENU_SCRAP.MENU,
        url: '/scrap',
        customIcon: <AllieCoinsStackedIcon />,
        permissionRequired: [
          findPermissionWithAction(scrapAndCostPage, PermissionAction.VIEW, permissions),
          PermissionAction.VIEW,
        ],
        children: [
          {
            title: MENU_SCRAP.SUB_MENU_ACCOUNT,
            url: '/scrap/data-entry',
            permissionRequired: [PermissionSubject.SCRAP, PermissionAction.CREATE],
          },
          isTest
            ? {
              title: MENU_SCRAP.SUB_MENU_PO_LIST,
              url: '/scrap/performance/po-list',
              permissionRequired: [PermissionSubject.SCRAP, PermissionAction.VIEW],
            } : {
              title: MENU_SCRAP.SUB_MENU_REPORTS,
              url: '/scrap/performance/by-line',
              permissionRequired: [PermissionSubject.SCRAP, PermissionAction.VIEW],
            },
          {
            title: MENU_SCRAP.SUB_MENU_COSTS,
            url: '/scrap/costs',
            permissionRequired: [PermissionSubject.COST, PermissionAction.VIEW],
          },
        ],
      },
    ];

    return basePages as AppPage[];
  };

  const filteredPages = useMemo(
    () => (isMobile ? getCommonPages() : getDesktopPages()),
    [isMobile, data, totalUnseenNotifications, loggedUser, permissions, isCompany]
  );

  return filteredPages;
};
